import type {RefObject} from 'react';
import React from 'react';

import styled from '@emotion/styled';
import Scrollbars from 'react-custom-scrollbars-2';

import type {WithChildren} from '../../../utils/childTypes';

/**
 * The `renderThumbVertical` function is used to render the vertical thumb for the custom scroll container.
 * This function returns a JSX element representing the vertical scroll thumb with a data attribute
 * for identifying it in tests.
 *
 * @returns A JSX element representing the vertical thumb of the custom scroll component.
 *
 * @example
 * ```tsx
 * const thumbElement = renderThumbVertical();
 * ```
 */
const renderThumbVertical = () => <VerticalThumb data-cy="customScroll-vertical-thumb" />;

/**
 * The `renderTrackVertical` function is used to render the vertical track for the custom scroll container.
 * This function returns a JSX element representing the vertical scroll track with a data attribute
 * for identifying it in tests.
 *
 * @returns A JSX element representing the vertical track of the custom scroll component.
 *
 * @example
 * ```tsx
 * const trackElement = renderTrackVertical();
 * ```
 */
const renderTrackVertical = () => <VerticalTrack data-cy="customScroll-vertical-track" />;

/**
 * The `ComponentProps` interface defines the properties for the `CustomScrollSelect` component.
 * This component is used to render a scrollable container with a customizable height and custom scroll behavior.
 * It accepts a scroll reference and provides a unique test identifier for testing purposes.
 */
export interface ComponentProps {
    /**
     * The height of the `CustomScrollSelect` component. This determines the maximum height
     * for the scrollable content area.
     */
    height?: number;
    /**
     * A reference object that will be passed to the Scrollbars component for scroll operations.
     * This allows external control and access to the scroll state of the container.
     */
    scrollRef: RefObject<Scrollbars | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'CustomScrollSelect'
     */
    testId?: string;
}

/**
 * The `CustomScrollSelect` component provides a custom scrollable container with a vertical scrollbar,
 * designed for specific height constraints and enhanced scrolling behavior. It includes custom rendering
 * for the scrollbar track and thumb, ensuring a smooth and visually consistent experience.
 *
 * @param props           The component props.
 * @param props.children  The content to be displayed inside the scrollable container.
 * @param props.height    The maximum height of the scrollable area.
 * @param props.scrollRef The ref object to track the scrollable container element.
 * @param props.testId    A unique identifier for testing purposes, useful for identifying the component in tests.
 * @returns A JSX element representing a scrollable container with custom vertical scroll track and thumb.
 *
 * @example
 * ```tsx
 * <CustomScrollSelect height={400} scrollRef={ref}>
 *   <OptionList />
 * </CustomScrollSelect>
 * ```
 */
const CustomScrollSelect = ({
    children,
    height,
    scrollRef,
    testId = 'CustomScrollSelect'
}: WithChildren<ComponentProps>) => (
    <Wrapper
        ref={scrollRef}
        autoHeightMax={height}
        data-cy={testId}
        renderThumbVertical={renderThumbVertical}
        renderTrackVertical={renderTrackVertical}
        autoHeight
        hideTracksWhenNotNeeded
        universal
    >
        {children}
    </Wrapper>
);

CustomScrollSelect.displayName = 'CustomScrollSelect';

export {CustomScrollSelect};

const VerticalThumb = styled.div`
    background-color: ${({theme}) => theme.colors.neutral20};
    border-radius: inherit;
    cursor: pointer;
    display: block;
    position: relative;
    width: 100%;
`;

const VerticalTrack = styled.div`
    border-radius: 2px;
    bottom: 8px;
    position: absolute;
    right: 4px;
    top: 8px;
    width: 4px;
`;

const Wrapper = styled(Scrollbars)`
    width: 100%;
`;